// Szenarien-Simulation (Finanzen → Szenarien, ADR 0043):
// Cashflow-Graph wie in der Cashflow-Ansicht, aber auf einer clientseitig
// transformierten Kopie der Budget-Einträge. Projekte und Team-Mitglieder
// können ab einem Datum "wegfallen", hypothetische Personen mit Lohn können
// hinzugefügt werden. NICHTS davon wird gespeichert — reine Ansicht.
import React, { useState, useEffect, useMemo, useRef } from "react";
import CashflowChart from "./CashflowChart";
import {
  getBudgetEntries,
  getCashflowWarnschwelle,
  calculateAverageMonthlyRecurringExpenses,
} from "../services/azureBudgetService";
import { generateDynamicMwstCashflowEntries } from "../services/mwstService";
import { getBookings } from "../services/azureBookingService";
import { getAllPhases } from "../services/azureProjectPhasesService";
import { getAllProjects } from "../services/azureProjectsService";
import { getAllTeamMembers } from "../services/azureTeamService";
import {
  getCurrentSalary,
  getAllPkManualEntries,
} from "../services/azureSalaryService";
import { getAllActiveRates } from "../services/azureInsuranceService";
import {
  calcAllDeductions,
  calcFAKNetto,
  getAgeAtDate,
} from "../utils/insuranceCalculations";
import {

  calculateCurrentBankSaldo,
  generateExtendedCashflow,
  findThresholdDates,
} from "../services/cashflowEngine";
import { formatCurrency } from "../utils/formatUtils";

const WOCHEN = 52;
const VISIBLE_WEEKS = 13;

const todayIso = () => new Date().toISOString().split("T")[0];

const isoMinusOneDay = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().split("T")[0];
};

const formatDatum = (dateLike) =>
  dateLike ? new Date(dateLike).toLocaleDateString("de-CH") : "";

// Eintrag ab einem Stichtag "abschneiden": ab abDatum fallen keine
// Zahlungen mehr an. Regelmässig -> bis_wann = Vortag; Einmalig -> Eintrag
// entfällt, wenn sein wirksames Datum (inkl. Zahlungsfrist) >= Stichtag ist.
const cutEntryAb = (entry, abDatum) => {
  if (entry.typ_timing === "Regelmässig") {
    if (entry.ab_wann && entry.ab_wann >= abDatum) return [];
    const vortag = isoMinusOneDay(abDatum);
    if (entry.bis_wann && entry.bis_wann <= vortag) return [entry];
    return [{ ...entry, bis_wann: vortag }];
  }

  const dateStr = entry.versand_datum || entry.wann;
  if (!dateStr) return [entry];
  const eff = new Date(dateStr);
  const zf = parseInt(entry.zahlungsfrist) || 0;
  if (zf > 0) eff.setDate(eff.getDate() + zf);
  return eff.toISOString().split("T")[0] >= abDatum ? [] : [entry];
};

// Aggregat-Eintrag (Sozialversicherungen/FAK) in Segmente teilen: pro
// Wegfall-Datum sinkt der Betrag um den Anteil der Person. Das erste Segment
// behält die Original-ID (Dedup gegen bezahlte Monats-Kopien).
const segmentAggregate = (entry, events) => {
  const relevant = events
    .filter((ev) => ev.betrag > 0)
    .sort((a, b) => (a.abDatum < b.abDatum ? -1 : 1));
  if (!relevant.length) return [entry];

  const segments = [];
  let betrag = entry.brutto_betrag || entry.betrag || 0;
  let segStart = entry.ab_wann || todayIso();
  let segIndex = 0;

  for (const ev of relevant) {
    if (entry.bis_wann && ev.abDatum > entry.bis_wann) break;
    if (ev.abDatum <= segStart) {
      betrag -= ev.betrag;
      continue;
    }
    segments.push({
      ...entry,
      id: segIndex === 0 ? entry.id : `${entry.id}_seg${segIndex}`,
      ab_wann: segStart,
      bis_wann: isoMinusOneDay(ev.abDatum),
    });
    segments[segments.length - 1].betrag = Math.max(0, Math.round(betrag * 100) / 100);
    segments[segments.length - 1].brutto_betrag = segments[segments.length - 1].betrag;
    segments[segments.length - 1].netto_betrag = segments[segments.length - 1].betrag;
    segIndex++;
    segStart = ev.abDatum;
    betrag -= ev.betrag;
  }

  const rest = Math.max(0, Math.round(betrag * 100) / 100);
  if (rest > 0) {
    segments.push({
      ...entry,
      id: segIndex === 0 ? entry.id : `${entry.id}_seg${segIndex}`,
      ab_wann: segStart,
      bis_wann: entry.bis_wann || "",
      betrag: rest,
      brutto_betrag: rest,
      netto_betrag: rest,
    });
  }

  return segments;
};

const CashflowSzenarien = () => {
  const [loading, setLoading] = useState(true);
  const [rechnend, setRechnend] = useState(false);

  // Basisdaten (unverändert, wie die Cashflow-Ansicht sie lädt)
  const [baseEntries, setBaseEntries] = useState([]);
  const [startSaldo, setStartSaldo] = useState(0);
  const [phaseNames, setPhaseNames] = useState({});
  const [warnschwelle, setWarnschwelle] = useState(0);
  const [projekte, setProjekte] = useState([]); // { code, title }
  const [projektPhasen, setProjektPhasen] = useState({}); // code -> [{ id, name }] in Reihenfolge
  const [personen, setPersonen] = useState([]); // { employeeUuid, name, nettoLohn, anAgAnteil, fakAnteil }
  const [rates, setRates] = useState(null);

  // Szenario-Zustand — NUR Ansicht, wird nie gespeichert
  const [projektSzenario, setProjektSzenario] = useState({}); // code -> { inaktiv, abDatum }
  const [projektPhasenSzenario, setProjektPhasenSzenario] = useState({}); // code -> phaseId ("ab dieser Phase gestrichen")
  const [personenSzenario, setPersonenSzenario] = useState({}); // uuid -> { inaktiv, abDatum }
  const [hypothetische, setHypothetische] = useState([]); // { id, name, brutto, pensum, abDatum }

  // Ergebnis-Wochen
  const [baselineWeeks, setBaselineWeeks] = useState([]);
  const [szenarioWeeks, setSzenarioWeeks] = useState([]);
  const [currentOffset, setCurrentOffset] = useState(0);

  const rechnungLaufNr = useRef(0);

  useEffect(() => {
    loadBasisDaten();
  }, []);

  const loadBasisDaten = async () => {
    setLoading(true);
    try {
      const heute = todayIso();
      const [
        entries,
        bookings,
        allPhases,
        storedWarnschwelle,
        allProjects,
        teamMembers,
        activeRates,
        pkManualEntries,
      ] = await Promise.all([
        getBudgetEntries(),
        getBookings(),
        getAllPhases(),
        getCashflowWarnschwelle(),
        getAllProjects(),
        getAllTeamMembers(),
        getAllActiveRates(heute),
        getAllPkManualEntries(),
      ]);

      const phaseMap = {};
      allPhases.forEach((p) => {
        if (p.id) phaseMap[p.id] = p.name || "";
      });

      // Phasen pro Projekt, sortiert nach Startdatum (für "ab Phase streichen").
      // BEWUSST inkl. localOnly-Phasen — sonst fehlen z.B. beim Gleispool die
      // lokal erfassten Phasen im Dropdown.
      const phasenProProjekt = {};
      allPhases
        .filter((p) => p.projectNumber)
        .sort((a, b) => ((a.startDate || "") < (b.startDate || "") ? -1 : 1))
        .forEach((p) => {
          (phasenProProjekt[p.projectNumber] ||= []).push({
            id: p.id,
            name: p.name || "",
            startDate: p.startDate || "",
            endDate: p.endDate || "",
          });
        });

      // Projekt hat eine "aktuelle" Phase: läuft jetzt (Start <= heute <= Ende
      // bzw. offenes Ende) oder beginnt in der Zukunft
      const hatAktuellePhase = (code) =>
        (phasenProProjekt[code] || []).some(
          (p) =>
            (p.endDate && p.endDate >= heute) ||
            (p.startDate && p.startDate >= heute) ||
            (p.startDate && p.startDate <= heute && !p.endDate)
        );

      const avgMonthly = calculateAverageMonthlyRecurringExpenses(entries);
      const schwelle =
        storedWarnschwelle ?? Math.round(avgMonthly * 3 * 100) / 100;

      // Projekte, die im Cashflow vorkommen (Budget-Einträge tragen den Code)
      // UND eine aktuelle oder zukünftige Phase haben — abgeschlossene
      // Projekte sind für Szenarien nicht relevant
      const codesInBudget = new Set(
        entries.map((e) => e.projekt).filter(Boolean)
      );
      const relevanteProjekte = allProjects
        .filter(
          (p) => p.code && codesInBudget.has(p.code) && hatAktuellePhase(p.code)
        )
        .map((p) => ({ code: p.code, title: p.title || "" }))
        .sort((a, b) => a.code.localeCompare(b.code));

      // Aktive Team-Mitglieder mit aktuellem Lohn + ihrem Anteil an den
      // Sozialabgaben-Aggregaten (für die Wegfall-Simulation)
      const pkManualMap = {};
      pkManualEntries.forEach((e) => {
        pkManualMap[e.teamMemberId] = e;
      });

      const aktiveMitglieder = teamMembers.filter(
        (m) =>
          m.employeeUuid &&
          m.start &&
          m.start <= heute &&
          (!m.ende || m.ende >= heute)
      );

      const personenListe = [];
      await Promise.all(
        aktiveMitglieder.map(async (member) => {
          const salary = await getCurrentSalary(member.employeeUuid);
          if (!salary) return;
          const lohn = salary.brutto || salary.netto || 0;
          const pensum = salary.pensum ?? member.pensum ?? 100;
          const effektiverBrutto = (lohn * pensum) / 100;
          if (effektiverBrutto <= 0) return;

          let anAgAnteil = 0;
          let fakAnteil = 0;
          let nettoLohn = effektiverBrutto;
          if (activeRates) {
            const alter = getAgeAtDate(member.geburtsdatum, new Date());
            const ded = calcAllDeductions(
              effektiverBrutto,
              member.geschlecht,
              alter,
              activeRates,
              { pkManuell: pkManualMap[member.employeeUuid] }
            );
            anAgAnteil = ded.deductions
              .filter((d) => d.type !== "fak")
              .reduce((sum, d) => sum + d.anAbzug + d.agKosten, 0);
            nettoLohn = ded.netto > 0 ? ded.netto : effektiverBrutto;
            if (activeRates.fak) {
              fakAnteil = calcFAKNetto(
                effektiverBrutto,
                member.kinder || [],
                activeRates.fak,
                new Date()
              ).netto;
            }
          }

          personenListe.push({
            employeeUuid: member.employeeUuid,
            name: member.name,
            nettoLohn: Math.round(nettoLohn * 100) / 100,
            anAgAnteil: Math.round(anAgAnteil * 100) / 100,
            fakAnteil: Math.round(fakAnteil * 100) / 100,
          });
        })
      );
      personenListe.sort((a, b) => a.name.localeCompare(b.name, "de"));

      setBaseEntries(entries);
      setStartSaldo(calculateCurrentBankSaldo(bookings));
      setPhaseNames(phaseMap);
      setWarnschwelle(schwelle);
      setProjekte(relevanteProjekte);
      setProjektPhasen(phasenProProjekt);
      setPersonen(personenListe);
      setRates(activeRates);

      // Ausgangslage einmal rechnen
      const heuteDate = new Date();
      const endDate = new Date(
        heuteDate.getTime() + WOCHEN * 7 * 24 * 60 * 60 * 1000
      );
      const mwstEntries = await generateDynamicMwstCashflowEntries(
        entries,
        heuteDate,
        endDate
      );
      const weeks = generateExtendedCashflow(
        entries,
        calculateCurrentBankSaldo(bookings),
        heuteDate,
        WOCHEN,
        mwstEntries,
        phaseMap
      );
      setBaselineWeeks(weeks);
      setSzenarioWeeks(weeks);
    } catch (error) {
      console.error("Fehler beim Laden der Szenario-Daten:", error);
    } finally {
      setLoading(false);
    }
  };

  // Monatskosten einer hypothetischen Person (Netto + Sozialabgaben = Brutto + AG).
  // Abgaben approximiert (Annahme: Alter 35; bei manuellem PK-Modus ohne PK).
  const hypothetischeKosten = (person) => {
    const brutto = parseFloat(person.brutto) || 0;
    const pensum = parseFloat(person.pensum) || 100;
    const effektiverBrutto = (brutto * pensum) / 100;
    if (effektiverBrutto <= 0) return null;

    if (!rates) {
      return { netto: effektiverBrutto, abgaben: 0, total: effektiverBrutto };
    }
    const ded = calcAllDeductions(effektiverBrutto, "M", 35, rates, {});
    const total = Math.round((effektiverBrutto + ded.totalAG) * 100) / 100;
    return {
      netto: ded.netto,
      abgaben: Math.round((total - ded.netto) * 100) / 100,
      total,
    };
  };

  // Szenario auf die Budget-Einträge anwenden (rein clientseitig)
  const transformierteEintraege = useMemo(() => {
    let entries = baseEntries;

    // 1a) Gestrichene Phasen: die gewählte UND alle späteren Phasen eines
    //     Projekts werden weder gezahlt noch bezahlt (Einträge entfallen ganz;
    //     das phase-Feld trägt teils Phasen-IDs, teils Klartext-Namen)
    Object.entries(projektPhasenSzenario).forEach(([code, phaseId]) => {
      if (!phaseId) return;
      const phasen = projektPhasen[code] || [];
      const startIdx = phasen.findIndex((p) => p.id === phaseId);
      if (startIdx < 0) return;
      const gestrichen = new Set();
      phasen.slice(startIdx).forEach((p) => {
        gestrichen.add(p.id);
        if (p.name) gestrichen.add(p.name);
      });
      entries = entries.filter(
        (e) => !(e.projekt === code && e.phase && gestrichen.has(e.phase))
      );
    });

    // 1b) Deaktivierte Projekte: ab Datum keine Ein-/Ausgaben des Projekts mehr
    Object.entries(projektSzenario).forEach(([code, cfg]) => {
      if (!cfg?.inaktiv || !cfg.abDatum) return;
      entries = entries.flatMap((e) =>
        e.projekt === code ? cutEntryAb(e, cfg.abDatum) : [e]
      );
    });

    // 2) Wegfallende Personen: Lohn/13. ML ab Datum weg + Anteil an den
    //    Sozialabgaben-Aggregaten reduziert
    const anAgEvents = [];
    const fakEvents = [];
    Object.entries(personenSzenario).forEach(([uuid, cfg]) => {
      if (!cfg?.inaktiv || !cfg.abDatum) return;
      const person = personen.find((p) => p.employeeUuid === uuid);
      entries = entries.flatMap((e) =>
        e.team_member_id === uuid ? cutEntryAb(e, cfg.abDatum) : [e]
      );
      if (person) {
        anAgEvents.push({ abDatum: cfg.abDatum, betrag: person.anAgAnteil });
        fakEvents.push({ abDatum: cfg.abDatum, betrag: person.fakAnteil });
      }
    });

    if (anAgEvents.length || fakEvents.length) {
      entries = entries.flatMap((e) => {
        if (e.team_member_id === "insurance_aggregated" && e.typ_timing === "Regelmässig") {
          return segmentAggregate(e, anAgEvents);
        }
        if (e.team_member_id === "fak_aggregated" && e.typ_timing === "Regelmässig") {
          return segmentAggregate(e, fakEvents);
        }
        return [e];
      });
    }

    // 3) Hypothetische Personen: ein synthetischer monatlicher Eintrag
    //    (Netto + Sozialabgaben) ab Datum
    const zusaetzliche = [];
    hypothetische.forEach((h) => {
      const kosten = hypothetischeKosten(h);
      if (!kosten || !h.abDatum) return;
      zusaetzliche.push({
        id: `szenario_hypo_${h.id}`,
        typ: "Ausgabe",
        name: `Szenario: Lohn ${h.name || "Neue Person"} (inkl. Sozialabgaben)`,
        konto: "5000",
        typ_timing: "Regelmässig",
        haeufigkeit: "monatlich",
        ab_wann: h.abDatum,
        bis_wann: "",
        wann: "",
        versand_datum: "",
        zahlungsfrist: "",
        betrag: kosten.total,
        brutto_betrag: kosten.total,
        netto_betrag: kosten.total,
        mwst_satz: 0,
        mwst_betrag: 0,
        ist_brutto: true,
        ist_prognose: false,
        status: "expecting",
        automatic_payment: false,
        projekt: "",
        phase: "",
        partner_name: "",
        team_member_id: "",
        lohn_eintrag_id: "",
        created_from_recurring_id: "",
        instance_period: "",
      });
    });

    return entries === baseEntries && !zusaetzliche.length
      ? baseEntries
      : [...entries, ...zusaetzliche];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseEntries, projektSzenario, projektPhasenSzenario, projektPhasen, personenSzenario, hypothetische, personen, rates]);

  // Szenario-Wochen neu rechnen, wenn sich die transformierten Einträge ändern
  useEffect(() => {
    if (loading || !baseEntries.length) return;

    const laufNr = ++rechnungLaufNr.current;
    const rechne = async () => {
      setRechnend(true);
      try {
        const heute = new Date();
        const endDate = new Date(heute.getTime() + WOCHEN * 7 * 24 * 60 * 60 * 1000);
        const mwstEntries = await generateDynamicMwstCashflowEntries(
          transformierteEintraege,
          heute,
          endDate
        );
        if (laufNr !== rechnungLaufNr.current) return; // veralteter Lauf
        const weeks = generateExtendedCashflow(
          transformierteEintraege,
          startSaldo,
          heute,
          WOCHEN,
          mwstEntries,
          phaseNames
        );
        setSzenarioWeeks(weeks);
      } catch (error) {
        console.error("Fehler beim Rechnen des Szenarios:", error);
      } finally {
        if (laufNr === rechnungLaufNr.current) setRechnend(false);
      }
    };
    rechne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformierteEintraege]);

  // KPI: Datum der Warnschwellen-Unterschreitung / des Negativ-Werdens
  const szenarioSchwellen = useMemo(
    () => findThresholdDates(szenarioWeeks, warnschwelle),
    [szenarioWeeks, warnschwelle]
  );
  const baselineSchwellen = useMemo(
    () => findThresholdDates(baselineWeeks, warnschwelle),
    [baselineWeeks, warnschwelle]
  );

  const hatSzenario =
    Object.values(projektSzenario).some((c) => c?.inaktiv && c.abDatum) ||
    Object.values(projektPhasenSzenario).some((phaseId) => Boolean(phaseId)) ||
    Object.values(personenSzenario).some((c) => c?.inaktiv && c.abDatum) ||
    hypothetische.some((h) => h.abDatum && (parseFloat(h.brutto) || 0) > 0);

  const handleScroll = (newOffset) => {
    const clamped = Math.max(
      0,
      Math.min(newOffset, Math.max(0, szenarioWeeks.length - VISIBLE_WEEKS))
    );
    setCurrentOffset(clamped);
  };

  // Pro Projekt gilt ENTWEDER Datum (Projekt austoggeln) ODER Phase — die
  // beiden Mechanismen schliessen sich gegenseitig aus
  const toggleProjekt = (code) => {
    setProjektSzenario((prev) => {
      const cfg = prev[code];
      const wirdInaktiv = !cfg?.inaktiv;
      if (wirdInaktiv) {
        setProjektPhasenSzenario((phasenPrev) => ({ ...phasenPrev, [code]: "" }));
      }
      return {
        ...prev,
        [code]: wirdInaktiv
          ? { inaktiv: true, abDatum: cfg?.abDatum || todayIso() }
          : { inaktiv: false, abDatum: cfg?.abDatum },
      };
    });
  };

  const waehlePhaseAb = (code, phaseId) => {
    setProjektPhasenSzenario((prev) => ({ ...prev, [code]: phaseId }));
    if (phaseId) {
      setProjektSzenario((prev) => ({
        ...prev,
        [code]: { inaktiv: false, abDatum: prev[code]?.abDatum },
      }));
    }
  };

  const togglePerson = (uuid) => {
    setPersonenSzenario((prev) => {
      const cfg = prev[uuid];
      return {
        ...prev,
        [uuid]: cfg?.inaktiv
          ? { inaktiv: false, abDatum: cfg.abDatum }
          : { inaktiv: true, abDatum: cfg?.abDatum || todayIso() },
      };
    });
  };

  const addHypothetische = () => {
    setHypothetische((prev) => [
      ...prev,
      {
        id: `${Date.now()}_${prev.length}`,
        name: "",
        brutto: "",
        pensum: "100",
        abDatum: todayIso(),
      },
    ]);
  };

  const updateHypothetische = (id, field, value) => {
    setHypothetische((prev) =>
      prev.map((h) => (h.id === id ? { ...h, [field]: value } : h))
    );
  };

  const removeHypothetische = (id) => {
    setHypothetische((prev) => prev.filter((h) => h.id !== id));
  };

  // "erstmals" = erste Woche unter der Schwelle (kann kurzer Einbruch sein,
  // z.B. durch nachgerückte offene Alt-Posten); "dauerhaft ab" = ab dieser
  // Woche bleibt der Saldo bis zum Horizont-Ende darunter
  const formatSchwellenWert = (erstmals, dauerhaft) => {
    if (!erstmals) return `nicht innerhalb ${WOCHEN} Wochen`;
    const erstmalsText = `${formatDatum(erstmals.weekStart)} (${erstmals.shortLabel})`;
    if (dauerhaft && dauerhaft.weekIndex !== erstmals.weekIndex) {
      return `erstmals ${erstmalsText}, dauerhaft ab ${formatDatum(dauerhaft.weekStart)} (${dauerhaft.shortLabel})`;
    }
    if (!dauerhaft) {
      return `erstmals ${erstmalsText}, erholt sich innerhalb ${WOCHEN} Wochen wieder`;
    }
    return erstmalsText;
  };

  const renderSchwellenZeile = (label, schwellen, betont) => (
    <div className={`szenario-kpi__zeile${betont ? " betont" : ""}`}>
      <span className="szenario-kpi__label">{label}</span>
      <span className="szenario-kpi__wert">
        Unter Warnschwelle ({formatCurrency(warnschwelle)}):{" "}
        <strong>
          {formatSchwellenWert(
            schwellen.unterschreitung,
            schwellen.unterschreitungDauerhaft
          )}
        </strong>
      </span>
      <span className="szenario-kpi__wert">
        Negativ:{" "}
        <strong>
          {formatSchwellenWert(schwellen.negativ, schwellen.negativDauerhaft)}
        </strong>
      </span>
    </div>
  );

  if (loading) {
    return <div className="cashflow-loading">Lade Szenario-Daten...</div>;
  }

  const visibleData = szenarioWeeks.slice(
    currentOffset,
    currentOffset + VISIBLE_WEEKS
  );

  return (
    <div className="cashflow-planung-scroll szenarien-view">
      <div className="szenario-hinweis">
        Simulation — Änderungen hier werden NICHT gespeichert und beeinflussen
        weder Budget noch Cashflow-Daten.
        {rechnend && <span className="szenario-hinweis__rechnend"> Rechne…</span>}
      </div>

      <div className="cashflow-chart-container-scroll">
        <CashflowChart
          data={visibleData}
          allData={szenarioWeeks}
          warnschwelle={warnschwelle}
          startingSaldo={startSaldo}
          onWeekClick={() => {}}
          currentOffset={currentOffset}
          onScroll={handleScroll}
          totalWeeks={szenarioWeeks.length}
        />
      </div>

      {/* KPI: Wann unterschreitet der Saldo die Warnschwelle / wird negativ */}
      <div className="szenario-kpi">
        {renderSchwellenZeile(
          hatSzenario ? "Mit Szenario" : "Aktuelle Planung",
          szenarioSchwellen,
          true
        )}
        {hatSzenario &&
          renderSchwellenZeile("Ausgangslage", baselineSchwellen, false)}
      </div>

      {/* Projekte ein-/austoggeln */}
      <section className="szenario-section">
        <h3>Projekte</h3>
        <p className="szenario-section__hint">
          Ausgetoggelte Projekte verursachen ab dem gewählten Datum keine
          Ein- und Ausgaben mehr. Alternativ lässt sich eine Phase abwählen:
          diese und alle späteren Phasen werden weder gezahlt noch bezahlt.
        </p>
        <div className="szenario-liste">
          {projekte.map((p) => {
            const cfg = projektSzenario[p.code];
            const inaktiv = cfg?.inaktiv === true;
            const gestrichenAb = projektPhasenSzenario[p.code] || "";
            // Abwählbar sind nur Phasen, die in der Zukunft beginnen —
            // Vergangenes lässt sich nicht mehr "streichen"
            const heuteStr = todayIso();
            const zukunftsPhasen = (projektPhasen[p.code] || []).filter(
              (ph) => ph.startDate && ph.startDate > heuteStr
            );
            return (
              <div
                key={p.code}
                className={`szenario-zeile${inaktiv ? " inaktiv" : ""}${
                  !inaktiv && gestrichenAb ? " teilweise" : ""
                }`}
              >
                <label className="szenario-zeile__toggle">
                  <input
                    type="checkbox"
                    checked={!inaktiv}
                    onChange={() => toggleProjekt(p.code)}
                  />
                  <span className="szenario-zeile__name">
                    {p.code}
                    {p.title ? ` - ${p.title}` : ""}
                  </span>
                </label>
                {/* ENTWEDER Datum (Projekt ausgetoggelt) ODER Phase abwählen */}
                {inaktiv ? (
                  <label className="szenario-zeile__datum">
                    keine Zahlungen ab
                    <input
                      type="date"
                      value={cfg?.abDatum || ""}
                      onChange={(e) =>
                        setProjektSzenario((prev) => ({
                          ...prev,
                          [p.code]: { inaktiv: true, abDatum: e.target.value },
                        }))
                      }
                    />
                  </label>
                ) : (
                  zukunftsPhasen.length > 0 && (
                    <label className="szenario-zeile__feld">
                      Phasen streichen ab
                      <select
                        value={gestrichenAb}
                        onChange={(e) => waehlePhaseAb(p.code, e.target.value)}
                      >
                        <option value="">—</option>
                        {zukunftsPhasen.map((phase) => (
                          <option key={phase.id} value={phase.id}>
                            {phase.name || phase.id}
                            {phase.startDate
                              ? ` (ab ${formatDatum(phase.startDate)})`
                              : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                  )
                )}
              </div>
            );
          })}
          {projekte.length === 0 && (
            <div className="empty-state">Keine Projekte mit Budget-Einträgen</div>
          )}
        </div>
      </section>

      {/* Team-Mitglieder ein-/austoggeln */}
      <section className="szenario-section">
        <h3>Team</h3>
        <p className="szenario-section__hint">
          Wegfallende Personen: ab dem Datum entfallen Lohn und 13. Monatslohn;
          die Sozialabgaben-Aggregate werden um den Anteil der Person reduziert.
        </p>
        <div className="szenario-liste">
          {personen.map((person) => {
            const cfg = personenSzenario[person.employeeUuid];
            const inaktiv = cfg?.inaktiv === true;
            return (
              <div
                key={person.employeeUuid}
                className={`szenario-zeile${inaktiv ? " inaktiv" : ""}`}
              >
                <label className="szenario-zeile__toggle">
                  <input
                    type="checkbox"
                    checked={!inaktiv}
                    onChange={() => togglePerson(person.employeeUuid)}
                  />
                  <span className="szenario-zeile__name">{person.name}</span>
                </label>
                <span className="szenario-zeile__kosten">
                  {formatCurrency(person.nettoLohn)} netto +{" "}
                  {formatCurrency(person.anAgAnteil + person.fakAnteil)} Abgaben/Mt.
                </span>
                {inaktiv && (
                  <label className="szenario-zeile__datum">
                    fällt weg ab
                    <input
                      type="date"
                      value={cfg?.abDatum || ""}
                      onChange={(e) =>
                        setPersonenSzenario((prev) => ({
                          ...prev,
                          [person.employeeUuid]: {
                            inaktiv: true,
                            abDatum: e.target.value,
                          },
                        }))
                      }
                    />
                  </label>
                )}
              </div>
            );
          })}
          {personen.length === 0 && (
            <div className="empty-state">Keine aktiven Personen mit Lohn</div>
          )}
        </div>
      </section>

      {/* Hypothetische Personen */}
      <section className="szenario-section">
        <h3>Hypothetische Personen</h3>
        <p className="szenario-section__hint">
          Zusätzliche Person mit Lohn simulieren (Monatskosten = Netto +
          Sozialabgaben; Abgaben approximiert
          {rates?.pk?.berechnungsModus === "manuell"
            ? ", ohne Pensionskasse (manueller PK-Modus)"
            : ""}
          ).
        </p>
        <div className="szenario-liste">
          {hypothetische.map((h) => {
            const kosten = hypothetischeKosten(h);
            return (
              <div key={h.id} className="szenario-zeile szenario-zeile--hypo">
                <input
                  type="text"
                  className="hypo-name"
                  placeholder="Name (optional)"
                  value={h.name}
                  onChange={(e) => updateHypothetische(h.id, "name", e.target.value)}
                />
                <label className="szenario-zeile__feld">
                  Brutto/Mt.
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={h.brutto}
                    onChange={(e) => updateHypothetische(h.id, "brutto", e.target.value)}
                    placeholder="z.B. 8000"
                  />
                </label>
                <label className="szenario-zeile__feld">
                  Pensum %
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="5"
                    value={h.pensum}
                    onChange={(e) => updateHypothetische(h.id, "pensum", e.target.value)}
                  />
                </label>
                <label className="szenario-zeile__feld">
                  ab
                  <input
                    type="date"
                    value={h.abDatum}
                    onChange={(e) => updateHypothetische(h.id, "abDatum", e.target.value)}
                  />
                </label>
                <span className="szenario-zeile__kosten">
                  {kosten
                    ? `${formatCurrency(kosten.total)}/Mt. (${formatCurrency(kosten.netto)} netto + ${formatCurrency(kosten.abgaben)} Abgaben)`
                    : "—"}
                </span>
                <button
                  className="budget-card__btn"
                  onClick={() => removeHypothetische(h.id)}
                >
                  Entfernen
                </button>
              </div>
            );
          })}
        </div>
        <button className="budget-card__btn budget-card__btn--primary" onClick={addHypothetische}>
          + Hypothetische Person hinzufügen
        </button>
      </section>
    </div>
  );
};

export default CashflowSzenarien;
